import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Modal from '@mui/material/Modal';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import React, { SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EHolidayType } from '../../../../shared/enums/holiday/EHoliday';
import { EReligion } from '../../../../shared/enums/user/EReligion';
import { ETheme } from '../../../../shared/enums/user/ETheme';
import { IHoliday } from '../../../../shared/interfaces/holiday/IHoliday';
import { IPagination } from '../../../../shared/interfaces/shared/IPagination';
import { IUser } from '../../../../shared/interfaces/user/IUser';
import { darkPalette, lightPalette } from '../../../../shared/mui/palettes';
import { RootState } from '../../../../store/store';

interface IProps {
  pagination: IHoliday;
  setPagination: React.Dispatch<SetStateAction<IPagination>>;
  changeFilterHandler: (name: string, value: string) => void;
}
const HolidayFilters: React.FC<IProps> = ({
  pagination,
  setPagination,
  changeFilterHandler,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { theme } = useSelector(
    (state: RootState) => state.auth.userData,
  ) as IUser;

  const { country: countries } = useSelector((state: RootState) => state.enums);

  const handleClose = () => {
    setOpen(false);
  };
  const resetFilters = () => {
    setPagination({ page: 1, perPage: 15 });
    handleClose();
  };

  return (
    <Box>
      <Button
        variant={'contained'}
        color={'secondary'}
        onClick={() => setOpen(true)}
      >
        <FilterAltOffOutlinedIcon />
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          className={'MuiModalBody'}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: { xs: '80%', lg: '30%' },
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              paddingBottom: '10px',
              borderBottom: `1px solid ${theme === ETheme.LIGHT ? lightPalette.primary.light : darkPalette.background.light}`,
            }}
          >
            <Typography id="modal-modal-title" variant="h6">
              {t('general.filterHolidays')}
            </Typography>
          </Box>
          <Grid container spacing={2} sx={{ margin: '20px 0', width: '100%' }}>
            <Grid size={12}>
              <TextField
                onChange={(e) => {
                  changeFilterHandler('name', e.target.value);
                }}
                label={t('general.searchByName')}
                defaultValue={pagination?.name}
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel>{t('holidays.searchByType')}</InputLabel>
                <Select
                  labelId={`user-label`}
                  id={`user-select`}
                  defaultValue={pagination?.holidayType}
                  label={t('holidays.searchByType')}
                  onChange={(e) => {
                    changeFilterHandler('holidayType', e.target.value);
                  }}
                >
                  {Object.values(EHolidayType)?.map((holidayType) => {
                    return (
                      <MenuItem key={holidayType} value={holidayType}>
                        {t(`holidays.type.${holidayType}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel>{t('general.searchByReligion')}</InputLabel>
                <Select
                  labelId={`user-label`}
                  id={`user-select`}
                  defaultValue={pagination?.religion}
                  label={t('general.searchByReligion')}
                  onChange={(e) => {
                    changeFilterHandler('religion', e.target.value);
                  }}
                >
                  {Object.values(EReligion)?.map((religion) => {
                    return (
                      <MenuItem key={religion} value={religion}>
                        {t(`religion.${religion}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl sx={{ width: '100%' }}>
                <InputLabel>{t('general.searchByCountry')}</InputLabel>
                <Select
                  labelId={`user-label`}
                  id={`user-select`}
                  defaultValue={pagination?.country}
                  label={t('general.searchByCountry')}
                  onChange={(e) => {
                    changeFilterHandler('country', e.target.value);
                  }}
                >
                  {countries?.map((country) => {
                    return (
                      <MenuItem key={country} value={country}>
                        {t(`country.${country}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <div>
            <Button
              variant={'contained'}
              color={'secondary'}
              onClick={handleClose}
              sx={{ marginRight: '10px' }}
            >
              {t('button.cancel')}
            </Button>
            <Button
              variant={'contained'}
              color={'primary'}
              onClick={resetFilters}
            >
              {t('button.clearFilters')}
            </Button>
          </div>
        </Box>
      </Modal>
    </Box>
  );
};

export default HolidayFilters;
